/**
 * Centralized text limits for the deck system.
 *
 * Limits are generated from the canonical slide manifest, which is compiled from
 * the kept slide schemas. Deck UI should consume them
 * through {@link getTextLimit} or {@link visibleTextClampFromSchema}.
 */

import { normalizeSlideTypeForLimits as normalizeSlideType } from "./slide-type-aliases";
import {
  GLOBAL_TEXT_LIMITS,
  ManifestTextLimit,
  getManifestTextLimits,
  hasManifestSchema,
} from "./slide-manifest";

/** Text limit definition */
export type TextLimit = {
  /** Maximum characters before truncation */
  maxChars: number;
  /** Minimum lines to reserve (for visual slot consistency) */
  minLines: number;
  /** Maximum lines before CSS line-clamp */
  maxLines: number;
};

/**
 * Schema-backed clamp props for `TextClamp` / sanitizer: always includes numeric
 * budgets from {@link getTextLimit}; `lineHeight` is presentation-only.
 */
export interface VisibleTextClampProps {
  minLines: number;
  maxLines: number;
  maxChars: number;
  lineHeight?: number | string;
}

/**
 * Merge `x-limits.text` budgets for `(field, slideType)` with optional presentation.
 */
export function visibleTextClampFromSchema(
  field: string,
  slideType: string | undefined,
  presentation?: { lineHeight?: number | string },
): VisibleTextClampProps {
  const limit = getTextLimit(field, slideType);
  return {
    minLines: limit.minLines,
    maxLines: limit.maxLines,
    maxChars: limit.maxChars,
    ...presentation,
  };
}

/**
 * The slide header chrome has no slide type of its own; use cover `title` / `eyebrow`
 * x-limits for those slots. Display headlines use exec-summary `actionTitle` / `actionSubtitle`
 * via {@link TEXT_CONSTRAINTS.slideActionTitle} in `SlideHeaderChrome`.
 */
export const SLIDE_HEADER_TEXT_REFERENCE_SLIDE_TYPE = "cover";

/** Default fallback for unknown fields */
const DEFAULT_TEXT_LIMIT: TextLimit = {
  maxChars: 500,
  minLines: 2,
  maxLines: 2,
};

function toTextLimit(limit: ManifestTextLimit): TextLimit {
  return {
    maxChars: limit.maxChars,
    minLines: limit.minLines ?? 1,
    maxLines: limit.maxLines ?? 3,
  };
}

/**
 * Get the text limit for a field from a slide's schema.
 *
 * @param field - The field name (e.g., 'title', 'itemName', 'quote')
 * @param slideType - The slide type (e.g., 'cover', 'exec-summary')
 * @returns The text limit from the schema's x-limits.text section
 *
 * @example
 * getTextLimit('title', 'cover') // { maxChars: 110, minLines: 3, maxLines: 4 }
 * getTextLimit('quoteText', 'single-select-bars') // { maxChars: 220, minLines: 2, maxLines: 3 }
 */
export function getTextLimit(field: string, slideType?: string): TextLimit {
  if (!slideType) {
    return DEFAULT_TEXT_LIMIT;
  }

  const resolved = normalizeSlideType(slideType);
  if (!hasManifestSchema(resolved)) {
    console.warn(`Unknown slide type: ${slideType}`);
    return DEFAULT_TEXT_LIMIT;
  }

  const textLimits = getManifestTextLimits(resolved);
  const limit = textLimits[field] ?? GLOBAL_TEXT_LIMITS[field];
  if (!limit) {
    return DEFAULT_TEXT_LIMIT;
  }

  return toTextLimit(limit);
}

/**
 * Get just the maxChars value for a field.
 * Convenience helper for truncation logic.
 */
export function getMaxChars(field: string, slideType?: string): number {
  return getTextLimit(field, slideType).maxChars;
}

/**
 * Get just the line constraints for a field.
 * Convenience helper for TextClamp components.
 */
export function getLineConstraints(
  field: string,
  slideType?: string,
): { minLines: number; maxLines: number } {
  const limit = getTextLimit(field, slideType);
  return { minLines: limit.minLines, maxLines: limit.maxLines };
}

/**
 * Raw manifest entry for `(field, slideType)`, ladder included.
 *
 * {@link getTextLimit} narrows to {@link TextLimit} and drops the ladder, so
 * {@link ladderMaxLines} needs the unnarrowed entry. Deliberately silent: the
 * unknown-slide-type warning stays with `getTextLimit` so resolving a ladder
 * cannot double-log it.
 */
function resolveManifestTextLimit(
  field: string,
  slideType: string | undefined,
): ManifestTextLimit | undefined {
  if (!slideType) {
    return undefined;
  }

  const resolved = normalizeSlideType(slideType);
  if (!hasManifestSchema(resolved)) {
    return undefined;
  }

  const textLimits = getManifestTextLimits(resolved);
  return textLimits[field] ?? GLOBAL_TEXT_LIMITS[field];
}

/**
 * Resolve a field's line budget for a given item count from its
 * `x-limits.text[field].maxLinesByItemCount` ladder.
 *
 * A ladder exists because one visual slot's height is shared by every row: the
 * more rows a list has, the fewer lines each row's prose can afford. Rungs are
 * ascending `{ upTo, maxLines }` pairs, and for `itemCount` the first rung with
 * `upTo >= itemCount` wins. A field with no ladder, an unknown slide type, or
 * an item count past the last rung falls back to the field's base `maxLines`.
 *
 * `0` is a real answer, not a missing one: it means the field renders no
 * element at all at that density. Callers must branch on it rather than treat
 * it as falsy-and-therefore-unset.
 *
 * This is deliberately separate from {@link visibleTextClampFromSchema}, whose
 * signature and return shape are unchanged. A caller that wants both applies
 * them together, e.g.
 * `Math.min(clamp.maxLines, ladderMaxLines(field, slideType, rows.length))`.
 *
 * An `itemCount` past the last rung takes that rung, the tightest one, since
 * `upTo` ascends while `maxLines` descends. This matches
 * `apps/python-api/slide_contracts.py::slide_text_max_lines`, whose docstring
 * spells out why: falling back to the base `maxLines` would be wrong in the
 * overflow direction, the one direction that actually reaches a reader (it
 * would grant MORE lines exactly when there are more items than any rung
 * anticipated).
 */
export function ladderMaxLines(
  field: string,
  slideType: string | undefined,
  itemCount: number,
): number {
  const baseMaxLines = getTextLimit(field, slideType).maxLines;

  const ladder = resolveManifestTextLimit(field, slideType)?.maxLinesByItemCount;
  if (!ladder || ladder.length === 0 || !Number.isFinite(itemCount)) {
    return baseMaxLines;
  }

  for (const rung of ladder) {
    if (itemCount <= rung.upTo) {
      return rung.maxLines;
    }
  }

  return ladder[ladder.length - 1].maxLines;
}

/**
 * Truncate text to the limit for a field.
 * Returns original text if within limit.
 */
export function truncateToLimit(
  text: string | null | undefined,
  field: string,
  slideType?: string,
): string {
  if (!text) return "";
  const maxChars = getMaxChars(field, slideType);
  if (text.length <= maxChars) return text;
  return text.slice(0, maxChars - 3) + "...";
}

/**
 * Check if a slide type has limits defined for a field.
 */
export function hasTextLimit(field: string, slideType: string): boolean {
  const textLimits = getManifestTextLimits(normalizeSlideType(slideType));
  return field in textLimits;
}

/**
 * Get all text limits for a slide type.
 */
export function getSlideTextLimits(
  slideType: string,
): Record<string, TextLimit> {
  const textLimits = getManifestTextLimits(normalizeSlideType(slideType));
  return Object.fromEntries(
    Object.entries(textLimits).map(([field, limit]) => [
      field,
      toTextLimit(limit),
    ]),
  );
}
